import type { EndpointMeta } from "@/apis/types";
import {
  createFetchAndHook,
  type FetchFactory,
  type HookFactory,
} from "@/shared/factories";
import { datesHelper } from "@/shared/utils";
import { wsfScheduleApiMeta } from "../apiMeta";
import {
  type TerminalsAndMatesByRouteInput,
  terminalsAndMatesByRouteInputSchema,
} from "./shared/terminals.input";
import {
  type TerminalMate,
  terminalMateSchema,
} from "./shared/terminals.output";

/**
 * Metadata for the fetchTerminalsAndMatesByRoute endpoint
 */
export const terminalsAndMatesByRouteMeta = {
  functionName: "fetchTerminalsAndMatesByRoute",
  endpoint: "/terminalsandmatesbyroute/{TripDate}/{RouteID}",
  inputSchema: terminalsAndMatesByRouteInputSchema,
  outputSchema: terminalMateSchema.array(),
  sampleParams: { TripDate: datesHelper.tomorrow(), RouteID: 9 },
  endpointDescription:
    "List valid terminal pairs for a specific route and trip date.",
} satisfies EndpointMeta<TerminalsAndMatesByRouteInput, TerminalMate[]>;

/**
 * Fetch function for retrieving valid terminal pairs for a specific route and trip date
 */
const { fetch, useHook } = createFetchAndHook<
  TerminalsAndMatesByRouteInput,
  TerminalMate[]
>({
  api: wsfScheduleApiMeta,
  endpoint: terminalsAndMatesByRouteMeta,
  getCacheStrategy: () =>
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require("./shared/terminals.endpoints").scheduleTerminalsGroup
      .cacheStrategy,
});

/**
 * Fetch function for retrieving valid terminal pairs for a specific route and trip date
 */
export const fetchTerminalsAndMatesByRoute: FetchFactory<
  TerminalsAndMatesByRouteInput,
  TerminalMate[]
> = fetch;

/**
 * React Query hook for retrieving valid terminal pairs for a specific route and trip date
 */
export const useTerminalsAndMatesByRoute: HookFactory<
  TerminalsAndMatesByRouteInput,
  TerminalMate[]
> = useHook;
